import React, { useMemo, forwardRef, useEffect, useState } from 'react';
import { Link } from "react-router-dom";
import AppContext from "./../../app-context";
import closeOnClickOutside from './../core/close-on-click-outside';
import { useAuth } from "./../../AuthContext";
import { PUBLIC_URL, PRODUCT_ID, AUTH_URL } from './../../const';
import resolveUserRole from "../utils/resolveUserRole";
import Dialog from './../core/dialogs';
import PlanCard from "../core/plan-card";
import InvoicePreviewDialog from "../core/invoice-preview";
import { socket, requestData } from "./../../socket";

const PLAN_STYLE_PRESETS = [
    {
        presetId: "starter",
        background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 60%, #eef2ff 100%)",
        isDark: false,
        badgeKey: null,
    },
    {
        presetId: "growth",
        background: "linear-gradient(135deg, #5b8def 0%, #7f6bff 55%, #a855f7 100%)",
        isDark: true,
        badgeKey: "popular",
    },
    {
        presetId: "enterprise",
        background: "linear-gradient(135deg, #0b1023 0%, #1b1f3b 50%, #2d1b69 100%)",
        isDark: true,
        badgeKey: "premium",
    },
];

const dict = {
    en: {
        userProfile: 'User Profile',
        myProfile: 'My Profile',
        accountSettings: 'Account Settings',
        myInbox: 'My Inbox',
        messages: 'Messages',
        upgrade: 'Upgrade',
        upgradeRestricted: 'Contact an administrator to upgrade.',
        logOut: 'Log Out',
        currentWorkspace: 'Current workspace',
        manageWorkspaces: 'Manage workspaces',
        noWorkspace: 'Not selected',
        choosePlanTitle: "Choose your next plan",
        choosePlanSubtitle: "Unlock deeper monitoring, richer automation and hands-on support tailored to your growth.",
        planStarterName: "Starter",
        planStarterDescription: "Perfect for individuals validating their ideas.",
        planGrowthName: "Growth",
        planGrowthDescription: "Designed for fast-moving product teams.",
        planEnterpriseName: "Enterprise",
        planEnterpriseDescription: "Premium experience for mission-critical operations.",
        badgePopular: "Popular",
        badgePremium: "Premium",
        perMonthShort: "/mo",
        btnSelectPlan: "Select plan",
        btnSelected: "Selected",
        selectedPlanLabel: "Selected plan",
        selectPlanPlaceholder: "Choose any plan to continue",
        continueWith: (plan) => `Continue with ${plan}`,
        maybeLater: "Maybe later",
        featureWebsites: (value) => `${value} monitored sites`,
        featureStatusPages: (value) => `${value} status pages`,
        featureAutomations: (value) => `${value} automation rules`,
        featureTeamSeats: (value) => `${value} team seats`,
        featureSupportEmail: "Email support",
        featureSupportPriority: "Priority chat & email",
        featureSupportDedicated: "Dedicated success manager",
        unlimitedValue: "Unlimited",
        upgradePlaceholder: "We’ll finalize the upgrade with you soon — API integration is on the way.",
        loadingPlans: "Loading the latest plans…",
        failedLoadPlans: "Unable to load plans right now. Please try again later.",
        invoiceCreated: "Invoice request sent! We’ll reach out shortly.",
        invoiceFailed: "Unable to create invoice. Please try again.",
        creatingInvoice: "Creating…",
        previewTitle: "Invoice details",
        previewAmount: "Amount",
        previewDue: "Due date",
        payInvoice: "Pay invoice",
    },
    ru: {
        userProfile: 'Профиль пользователя',
        myProfile: 'Мой профиль',
        accountSettings: 'Настройки аккаунта',
        myInbox: 'Мой почтовый ящик',
        messages: 'Сообщения',
        upgrade: 'Обновить',
        upgradeRestricted: 'Свяжитесь с администратором для обновления.',
        logOut: 'Выйти',
        currentWorkspace: 'Текущий воркспейс',
        manageWorkspaces: 'Управление воркспейсами',
        noWorkspace: 'Не выбран',
        choosePlanTitle: "Выберите тариф",
        choosePlanSubtitle: "Раскройте расширенный мониторинг, больше автоматизаций и персональную поддержку.",
        planStarterName: "Старт",
        planStarterDescription: "Идеально для индивидуальных проектов и MVP.",
        planGrowthName: "Рост",
        planGrowthDescription: "Для продуктовых команд, растущих каждую неделю.",
        planEnterpriseName: "Энтерпрайз",
        planEnterpriseDescription: "Максимальные возможности и персональное сопровождение.",
        badgePopular: "Популярно",
        badgePremium: "Премиум",
        perMonthShort: "/мес",
        btnSelectPlan: "Выбрать тариф",
        btnSelected: "Выбран",
        selectedPlanLabel: "Выбранный тариф",
        selectPlanPlaceholder: "Выберите тариф для продолжения",
        continueWith: (plan) => `Продолжить с «${plan}»`,
        maybeLater: "Позже",
        featureWebsites: (value) => `${value} сайтов под мониторингом`,
        featureStatusPages: (value) => `${value} статус-страниц`,
        featureAutomations: (value) => `${value} правил автоматизации`,
        featureTeamSeats: (value) => `${value} мест в команде`,
        featureSupportEmail: "Поддержка по email",
        featureSupportPriority: "Приоритетный чат и email",
        featureSupportDedicated: "Выделенный менеджер",
        unlimitedValue: "Без лимита",
        upgradePlaceholder: "Скоро подключим API и завершим оформление вместе с вами.",
        loadingPlans: "Загружаем актуальные тарифы…",
        failedLoadPlans: "Не удалось загрузить тарифы. Попробуйте немного позже.",
        invoiceCreated: "Запрос на счёт отправлен! Мы скоро свяжемся.",
        invoiceFailed: "Не удалось создать счёт. Попробуйте ещё раз.",
        creatingInvoice: "Создаём…",
        previewTitle: "Детали счёта",
        previewAmount: "Сумма",
        previewDue: "Дата оплаты",
        payInvoice: "Оплатить счёт",
    },
    ua: {
        userProfile: 'Профіль користувача',
        myProfile: 'Мій профіль',
        accountSettings: 'Налаштування облікового запису',
        myInbox: 'Моя пошта',
        messages: 'Повідомлення',
        upgrade: 'Оновити',
        upgradeRestricted: 'Зверніться до адміністратора для оновлення.',
        logOut: 'Вийти',
        currentWorkspace: 'Поточний воркспейс',
        manageWorkspaces: 'Керувати воркспейсами',
        noWorkspace: 'Не вибрано',
        choosePlanTitle: "Оберіть тариф",
        choosePlanSubtitle: "Отримайте розширений моніторинг, більше автоматизацій та персональну підтримку.",
        planStarterName: "Старт",
        planStarterDescription: "Підходить для індивідуальних проєктів та MVP.",
        planGrowthName: "Зростання",
        planGrowthDescription: "Для команд, що швидко розвивають продукт.",
        planEnterpriseName: "Преміум",
        planEnterpriseDescription: "Максимальні можливості та персональний супровід.",
        badgePopular: "Популярно",
        badgePremium: "Преміум",
        perMonthShort: "/міс",
        btnSelectPlan: "Обрати тариф",
        btnSelected: "Обрано",
        selectedPlanLabel: "Обраний тариф",
        selectPlanPlaceholder: "Оберіть тариф, щоб продовжити",
        continueWith: (plan) => `Продовжити з «${plan}»`,
        maybeLater: "Пізніше",
        featureWebsites: (value) => `${value} сайтів під моніторингом`,
        featureStatusPages: (value) => `${value} статус-сторінок`,
        featureAutomations: (value) => `${value} правил автоматизації`,
        featureTeamSeats: (value) => `${value} робочих місць`,
        featureSupportEmail: "Підтримка email",
        featureSupportPriority: "Пріоритетний чат та email",
        featureSupportDedicated: "Виділений менеджер",
        unlimitedValue: "Без обмежень",
        upgradePlaceholder: "Невдовзі додамо API і завершимо оформлення разом із вами.",
        loadingPlans: "Завантажуємо актуальні тарифи…",
        failedLoadPlans: "Не вдалося завантажити тарифи. Спробуйте трохи пізніше.",
        invoiceCreated: "Запит на рахунок відправлено! Ми скоро зв’яжемося.",
        invoiceFailed: "Не вдалося створити рахунок. Спробуйте знову.",
        creatingInvoice: "Створюємо…",
        previewTitle: "Деталі рахунку",
        previewAmount: "Сума",
        previewDue: "Дата оплати",
        payInvoice: "Оплатити рахунок",
    },
};

const MenuUserProfile = forwardRef(({ open, setOpen, lang = 'en' }, ref) => {
    const { user, logout, refreshUser } = useAuth();
    const t = useMemo(() => dict[lang] || dict.en, [lang]);
    const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
    const [selectedPlanId, setSelectedPlanId] = useState(null);
    const [pricingPlans, setPricingPlans] = useState([]);
    const [plansLoading, setPlansLoading] = useState(false);
    const [plansError, setPlansError] = useState(null);
    const [plansRequested, setPlansRequested] = useState(false);
    const [invoiceSubmitting, setInvoiceSubmitting] = useState(false);
    const [invoiceError, setInvoiceError] = useState(null);
    const [upgradeNotice, setUpgradeNotice] = useState(null);
    const [previewInvoice, setPreviewInvoice] = useState(null);
    const [payingInvoice, setPayingInvoice] = useState(false);
    const [payError, setPayError] = useState(null);
    const openClass = open ? "show" : "";
    const userRole = useMemo(() => resolveUserRole(user), [user]);

    const planName = user?.pricing_plan?.name || t.selectPlanPlaceholder || "Choose any plan";
    const expirationISO = user?.pricing_plan?.expiration || null;
    const expirationTs = expirationISO ? Date.parse(expirationISO) : null;
    const isExpired = expirationTs ? expirationTs < Date.now() : false;
    const userEmail = user?.email || AppContext.user?.email || "";

    const formatDateTime = (value, fallback = "-") => {
        if (!value) return fallback;
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return fallback;
        return date.toLocaleString();
    };

    const getAmountLabel = (amount, currency) => {
        if (amount === undefined || amount === null || amount === "") return "-";
        const num = Number(amount);
        const formatted = Number.isNaN(num) ? amount : num.toFixed(2);
        return `${currency || "$"}${formatted}`;
    };

    const StatusBadge = ({ status }) => {
        const normalized = (status || '').toLowerCase();
        const colors = {
            pending: { background: '#FFC107', color: '#000000' },
            paid: { background: '#4CAF50', color: '#FFFFFF' },
            canceled: { background: '#F44336', color: '#FFFFFF' },
            unknown: { background: '#9E9E9E', color: '#FFFFFF' },
        };
        const labels = {
            pending: "Pending",
            paid: "Paid",
            canceled: "Canceled",
            unknown: "Unknown",
        };
        const style = {
            display: 'inline-block',
            padding: '5px 10px',
            borderRadius: 20,
            fontWeight: 'bold',
            fontSize: 14,
            textAlign: 'center',
            ...(colors[normalized] || colors.unknown),
        };
        return <span style={style}>{labels[normalized] || labels.unknown}</span>;
    };

    const formatStatus = (invoice) => <StatusBadge status={invoice?.status} />;
    const isInvoicePending = (invoice) => ((invoice?.status || "").toLowerCase() === "pending");

    const parseInvoiceMeta = (meta) => {
        if (!meta) return {};
        if (typeof meta === "object") return meta;
        try {
            return JSON.parse(meta);
        } catch {
            return {};
        }
    };

    const getPlanDescription = (invoice) => {
        const metaObj = parseInvoiceMeta(invoice.meta);
        return invoice.description || metaObj?.plan_description || "";
    };

    const extractPlanFeatures = (meta) => {
        const metaObj = parseInvoiceMeta(meta);
        const features = metaObj?.plan_features;
        if (Array.isArray(features)) {
            return features.filter(Boolean);
        }
        const display = metaObj?.plan_options?.display;
        if (Array.isArray(display)) {
            return display
                .map((item) => {
                    if (!item) return null;
                    const label = item.label || "";
                    const value = item.value || "";
                    return `${label}${label && value ? ": " : ""}${value}`.trim();
                })
                .filter(Boolean);
        }
        const program = metaObj?.plan_options?.program;
        if (program && typeof program === "object") {
            return Object.entries(program)
                .map(([key, value]) => `${key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())}: ${value}`)
                .filter(Boolean);
        }
        return [];
    };

    const formatDate = (iso) => {
        try {
            return new Date(iso).toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "2-digit"
            });
        } catch {
            return "";
        }
    };

    const showUpgrade = userRole !== "member";
    const currentWorkspaceName =
        user?.workspace?.name ||
        user?.workspace?.company_name ||
        user?.work_section ||
        user?.company?.name ||
        user?.company_name ||
        user?.workspace_name ||
        "";

    const planOptions = useMemo(() => {
        if (!pricingPlans.length) return [];

        const normalizeFeatures = (plan) => {
            const displayItems = Array.isArray(plan?.options?.display) ? plan.options.display : [];
            if (displayItems.length) {
                return displayItems.map((item) => `${item.label}: ${item.value}`);
            }
            const programEntries = Object.entries(plan?.options?.program || {});
            if (programEntries.length) {
                return programEntries.map(
                    ([key, value]) => `${key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())}: ${value}`
                );
            }
            return [];
        };

        const formatPriceLabel = (plan) => {
            if (plan?.price === null || typeof plan?.price === "undefined" || plan?.price === "") {
                return null;
            }
            const amount = Number(plan.price);
            const formatted = Number.isNaN(amount)
                ? plan.price
                : amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
            return `${plan.currency || ""}${formatted}`;
        };

        return pricingPlans.map((plan, index) => {
            const preset = PLAN_STYLE_PRESETS[index % PLAN_STYLE_PRESETS.length] || PLAN_STYLE_PRESETS[PLAN_STYLE_PRESETS.length - 1];
            const features = normalizeFeatures(plan);
            return {
                ...preset,
                id: `${plan.id ?? `plan-${index}`}`,
                name: plan.name || "",
                description: plan.description || "",
                price: formatPriceLabel(plan),
                period: plan.period || "",
                features,
                badge: preset.badgeKey === "popular" ? t.badgePopular : preset.badgeKey === "premium" ? t.badgePremium : null,
                raw: plan,
            };
        });
    }, [pricingPlans, t]);

    useEffect(() => {
        if (!planOptions.length) {
            if (selectedPlanId) {
                setSelectedPlanId(null);
            }
            return;
        }
        if (!selectedPlanId) return;
        const exists = planOptions.some((plan) => plan.id === selectedPlanId);
        if (!exists) {
            setSelectedPlanId(null);
        }
    }, [planOptions, selectedPlanId]);

    const selectedPlan = useMemo(() => {
        if (!selectedPlanId) return null;
        return planOptions.find((plan) => plan.id === selectedPlanId) || null;
    }, [planOptions, selectedPlanId]);

    const closeUpgradeModal = () => setUpgradeModalOpen(false);

    const cancelExistingInvoices = async () => {
        try {
            const response = await fetch(`${AUTH_URL}billing/invoices/cancel-all`, {
                method: "POST",
                headers: {
                    Authorization: user.token,
                    "Content-Type": "application/json",
                    "Accept-Language": lang,
                },
                body: JSON.stringify({
                    product_id: PRODUCT_ID,
                    workspace_id: user?.workspace_id || user?.workspace?.id || undefined,
                }),
            });
            const data = await response.json();
            if (!response.ok || data?.status !== 1) {
                throw new Error(data?.err?.message || data?.message || "Unable to cancel invoices");
            }
        } catch (err) {
            console.warn("Failed to cancel previous invoices:", err?.message || err);
        }
    };

    const handleConfirmPlan = async () => {
        if (!selectedPlan || invoiceSubmitting) return;

        setInvoiceSubmitting(true);
        setInvoiceError(null);

        const dueDate = new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString();
        const rawPlan = selectedPlan.raw || {};
        const parseAmount = () => {
            if (typeof rawPlan.price === "number") return rawPlan.price;
            if (typeof rawPlan.price === "string") {
                const parsed = parseFloat(rawPlan.price);
                if (!Number.isNaN(parsed)) return parsed;
            }
            if (typeof selectedPlan.price === "string") {
                const sanitized = parseFloat(selectedPlan.price.replace(/[^\d.,-]/g, "").replace(",", "."));
                if (!Number.isNaN(sanitized)) return sanitized;
            }
            return 0;
        };

        const payload = {
            product_id: PRODUCT_ID,
            workspace_id: user?.workspace_id || user?.workspace?.id || undefined,
            pricing_plan_id: rawPlan.id,
            amount: parseAmount(),
            currency: rawPlan.currency || "$",
            period: rawPlan.period || selectedPlan.period,
            description: rawPlan.description || selectedPlan.description || selectedPlan.name,
            due_date: dueDate,
            meta: {
                plan_name: selectedPlan.name,
                plan_id: rawPlan.id,
                plan_description: selectedPlan.description || rawPlan.description || "",
                plan_features: Array.isArray(selectedPlan.features) ? selectedPlan.features : [],
                source: "profile-upgrade-dialog",
            },
        };

        try {
            await cancelExistingInvoices();

            const response = await fetch(`${AUTH_URL}billing/invoices`, {
                method: "POST",
                headers: {
                    Authorization: user.token,
                    "Content-Type": "application/json",
                    "Accept-Language": lang,
                },
                body: JSON.stringify(payload),
            });
            const data = await response.json();

            if (!response.ok || data?.status !== 1) {
                throw new Error(data?.err?.message || data?.message || t.invoiceFailed);
            }

            const createdInvoice = data?.invoice || data?.data || null;

            setUpgradeModalOpen(false);
            setSelectedPlanId(null);
            if (typeof window !== "undefined" && window.dispatchEvent) {
                window.dispatchEvent(new CustomEvent("billing:invoices:refreshCounters", { detail: { reason: "created" } }));
            }

            if (createdInvoice) {
                setPreviewInvoice(createdInvoice);
                const numericAmount = Number(createdInvoice.amount ?? createdInvoice.pricing_plan_price);
                if (!Number.isNaN(numericAmount) && numericAmount === 0) {
                    handlePayInvoice(createdInvoice);
                }
            }

            if (typeof window !== "undefined" && window.location?.pathname?.includes("invoices")) {
                const targetPage = AppContext.pages?.invoices?.pageNum || 1;
                requestData(
                    user,
                    "invoices",
                    { page: targetPage, items_count: AppContext.rows_per_page },
                    "list"
                );
            }
        } catch (err) {
            console.error("Invoice creation failed", err);
            setInvoiceError(err?.message || t.invoiceFailed);
        } finally {
            setInvoiceSubmitting(false);
        }
    };

    const handleOpenUpgradeModal = () => {
        setOpen(false);
        setUpgradeModalOpen(true);
        setInvoiceError(null);
    };

    const handlePlanSelect = (planId) => {
        setSelectedPlanId(planId);
        setInvoiceError(null);
    };

    const handlePayInvoice = (invoice) => {
        if (!invoice || payingInvoice) return;
        const invoiceId = invoice.invoice_code || invoice.id;
        if (!invoiceId || !user?.token) {
            setPayError(t.invoiceFailed);
            return;
        }
        setPayError(null);
        setPayingInvoice(true);
        let settled = false;

        const requestId = `invoices:pay-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const responseEvent = "invoices:pay-response";

        const handleResponse = (payload = {}) => {
            if (payload.request_id !== requestId) return;
            socket.off(responseEvent, handleResponse);
            setPayingInvoice(false);
            settled = true;
            if (payload.status === 1) {
                const updatedInvoice = payload.invoice || payload.data || invoice;
                setPreviewInvoice(updatedInvoice);
                window.dispatchEvent?.(new CustomEvent("billing:invoices:refreshCounters", { detail: { reason: "paid" } }));
                refreshUser?.();
                if (typeof window !== "undefined" && window.location?.pathname?.includes("invoices")) {
                    const targetPage = AppContext.pages?.invoices?.pageNum || 1;
                    requestData(
                        user,
                        "invoices",
                        { page: targetPage, items_count: AppContext.rows_per_page },
                        "list"
                    );
                }
            } else {
                setPayError(payload?.err?.message || t.invoiceFailed);
            }
        };

        socket.on(responseEvent, handleResponse);
        socket.emit("invoices:pay", {
            request_id: requestId,
            token: user.token,
            invoice_id: invoiceId,
            lang,
        });

        setTimeout(() => {
            socket.off(responseEvent, handleResponse);
            if (!settled) {
                setPayingInvoice(false);
                setPayError(t.invoiceFailed);
            }
        }, 10000);
    };

    const closePreview = () => {
        setPreviewInvoice(null);
        setPayError(null);
        setPayingInvoice(false);
    };

    useEffect(() => {
        if (!upgradeModalOpen) {
            setPlansRequested(false);
        }
    }, [upgradeModalOpen]);

    useEffect(() => {
        if (!upgradeModalOpen) return undefined;
        const responseEvent = "pricing_plans:list-data";

        const handlePlansData = (payload = {}) => {
            setPlansLoading(false);
            if (payload?.status === 1) {
                const plans =
                    (Array.isArray(payload?.plans) && payload.plans) ||
                    (Array.isArray(payload?.data?.plans) && payload.data.plans) ||
                    (Array.isArray(payload?.data) && payload.data) ||
                    [];
                setPricingPlans(plans);
                setPlansError(null);
            } else if (payload) {
                setPlansError(payload?.err?.message || t.failedLoadPlans);
            }
        };

        socket.on(responseEvent, handlePlansData);

        return () => {
            socket.off(responseEvent, handlePlansData);
        };
    }, [upgradeModalOpen, t.failedLoadPlans]);

    useEffect(() => {
        if (!upgradeModalOpen || plansRequested) return;
        if (!user?.token) return;

        setPlansRequested(true);
        setPlansLoading(true);
        setPlansError(null);

        requestData(
            user,
            "pricing_plans",
            { product_id: PRODUCT_ID, lang },
            "list"
        );
    }, [upgradeModalOpen, plansRequested, user, lang]);

    useEffect(() => {
        const body = document.body;
        if (!body) return undefined;
        if (open) {
            body.classList.add("profile-menu-open");
        } else {
            body.classList.remove("profile-menu-open");
        }
        return () => body.classList.remove("profile-menu-open");
    }, [open]);

    return (
        <>
            <a
                className="nav-link pe-0"
                href="javascript:void(0)"
                onClick={() => setOpen(!open)}
                id="drop1"
                data-bs-toggle="dropdown"
                aria-expanded="false"
            >
                <div className="d-flex align-items-center">
                    <div className="user-profile-img">
                        <img
                            src={`${PUBLIC_URL}/assets/images/profile/user-1.jpg`}
                            className="rounded-circle"
                            width="35"
                            height="35"
                            alt=""
                        />
                    </div>
                </div>
            </a>
            <div
                className={`dropdown-menu content-dd dropdown-menu-end dropdown-menu-animate-up ${openClass}`}
                aria-labelledby="drop1"
                data-bs-popper="static"
                ref={ref}
            >
                <div className="profile-dropdown position-relative" data-simplebar="init">
                    <div className="simplebar-wrapper" style={{ margin: 0 }}>
                        <div className="simplebar-height-auto-observer-wrapper">
                            <div className="simplebar-height-auto-observer" />
                        </div>
                        <div className="simplebar-mask">
                            <div className="simplebar-offset" style={{ right: 0, bottom: 0 }}>
                                <div
                                    className="simplebar-content-wrapper"
                                    tabIndex="0"
                                    role="region"
                                    aria-label="scrollable content"
                                    style={{ height: "auto", overflow: "hidden" }}
                                >
                                    <div className="simplebar-content" style={{ padding: 0 }}>
                                        <div className="py-3 px-7 pb-0">
                                            <h5 className="mb-0 fs-5 fw-semibold">{t.userProfile}</h5>
                                        </div>

                                        <div className="d-flex align-items-center py-9 border-bottom" style={{ paddingLeft: 20 }}>
                                            <img
                                                src={`${PUBLIC_URL}/assets/images/profile/user-1.jpg`}
                                                className="rounded-circle"
                                                width="80"
                                                height="80"
                                                alt=""
                                            />
                                            <div className="ms-3">
                        <span className="mb-1 d-block fw-semibold fs-4">
                            {user.first_name} {user.last_name}
                        </span>
                                                <span className="mb-1 d-block text-body-secondary">{user.user_type}</span>
                                                {userEmail ? (
                                                    <p className="mb-0 d-flex align-items-center gap-2">
                                                        <i className="ti ti-mail fs-4"></i> {userEmail}
                                                    </p>
                                                ) : null}
                                            </div>
                                        </div>

                                        <div className="message-body">
                                            <Link
                                                to="/my-profile/1"
                                                className="py-8 px-7 mt-8 d-flex align-items-center"
                                                onClick={() => setOpen(false)}
                                            >
                                                <span className="d-flex align-items-center justify-content-center text-bg-light rounded-1 p-6">
                                                    <img src={`${PUBLIC_URL}/assets/images/svgs/icon-account.svg`} alt="" width="24" height="24" />
                                                </span>
                                                <div className="w-75 d-inline-block v-middle ps-3">
                                                    <h6 className="mb-1 fs-3 fw-semibold lh-base">{t.myProfile}</h6>
                                                    <span className="fs-2 d-block text-body-secondary">{t.accountSettings}</span>
                                                </div>
                                            </Link>

                                            <Link
                                                to="/tickets"
                                                className="py-8 px-7 mt-8 d-flex align-items-center"
                                                onClick={() => setOpen(false)}
                                            >
                                                <span className="d-flex align-items-center justify-content-center text-bg-light rounded-1 p-6">
                                                    <img src={`${PUBLIC_URL}/assets/images/svgs/icon-inbox.svg`} alt="" width="24" height="24" />
                                                </span>
                                                <div className="w-75 d-inline-block v-middle ps-3">
                                                    <h6 className="mb-1 fs-3 fw-semibold lh-base">{t.myInbox}</h6>
                                                    <span className="fs-2 d-block text-body-secondary">{t.messages}</span>
                                                </div>
                                            </Link>
                                        </div>

                                        <div className="px-7 py-4 border-bottom d-lg-none">
                                            <p className="text-uppercase text-body-secondary fw-semibold fs-2 mb-1">
                                                {t.currentWorkspace}
                                            </p>
                                            <h6 className="mb-2 fs-3 fw-semibold">
                                                {currentWorkspaceName || t.noWorkspace}
                                            </h6>
                                            {!currentWorkspaceName && (
                                                <span className="d-block mb-3 text-body-secondary fs-2">{t.noWorkspace}</span>
                                            )}
                                            <Link
                                                to="/workspaces"
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() => setOpen(false)}
                                            >
                                                {t.manageWorkspaces}
                                            </Link>
                                        </div>

                                        <div className="d-grid py-4 px-7 pt-8" style={{ marginBottom: "0px" }}>
                                            <div className="upgrade-plan bg-primary-subtle position-relative overflow-hidden rounded-4 p-4">
                                                <div className="row">
                                                    <div className="col-6">
                                                        <h5 className="fs-4 fw-semibold" style={{ marginBottom: "6px" }}>
                                                            {planName}
                                                        </h5>
                                                        <span
                                                            className="text-body-secondary d-block"
                                                            style={{ marginBottom: "20px", fontSize: "12px", fontWeight: "600", minHeight: "18px" }}
                                                        >
                                                            {expirationTs ? formatDate(expirationISO) : ""}
                                                        </span>
                                                        {showUpgrade ? (
                                                            userRole !== "member" ? (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-primary"
                                                                    onClick={handleOpenUpgradeModal}
                                                                >
                                                                    {t.upgrade}
                                                                </button>
                                                            ) : (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-primary"
                                                                    title={t.upgradeRestricted}
                                                                    disabled
                                                                    style={{ cursor: "not-allowed" }}
                                                                >
                                                                    {t.upgrade}
                                                                </button>
                                                            )
                                                        ) : null}
                                                    </div>
                                                    <div className="col-6">
                                                        <div className="m-n4 unlimited-img">
                                                            <img
                                                                src={`${PUBLIC_URL}/assets/images/backgrounds/unlimited-bg.png`}
                                                                alt=""
                                                                className="w-100"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{ paddingLeft: "30px", paddingRight: "30px" }}>
                                            <a
                                                href="javascript:void(0)"
                                                onClick={logout}
                                                className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center gap-2"
                                                style={{ minHeight: "48px", marginTop: "0" }}
                                            >
                                                <i className="ti ti-logout"></i>
                                                {t.logOut}
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="simplebar-placeholder" style={{ width: 360, height: 603 }} />
                    </div>

                    <div className="simplebar-track simplebar-horizontal" style={{ visibility: "hidden" }}>
                        <div className="simplebar-scrollbar" style={{ width: 0, display: "none" }} />
                    </div>
                    <div className="simplebar-track simplebar-vertical" style={{ visibility: "hidden" }}>
                        <div className="simplebar-scrollbar" style={{ height: 0, display: "none" }} />
                    </div>
                </div>
            </div>
                {upgradeModalOpen && (
                    <Dialog
                        title={t.choosePlanTitle}
                        editTitle={t.choosePlanTitle}
                        customTitle={t.choosePlanTitle}
                        dialogMaxWidth="1300px"
                        fieldsData={{}}
                        closeDialog={closeUpgradeModal}
                        saveDialog={handleConfirmPlan}
                        dialogSize="modal-xl modal-dialog-centered"
                        footerContent={
                            <div className="d-flex flex-wrap align-items-center gap-3 w-100">
                                <div className="flex-grow-1">
                                    <div className="text-uppercase text-body-secondary fw-semibold fs-2 mb-1">
                                        {t.selectedPlanLabel}
                                    </div>
                                    {selectedPlan ? (
                                        <>
                                            <div className="d-flex flex-wrap align-items-baseline gap-3 mb-1">
                                                <span className="display-6 fw-semibold text-primary mb-0">
                                                    {selectedPlan.name}
                                                </span>
                                                <span className="fs-4 fw-semibold text-dark">
                                                    {selectedPlan.price}
                                                    <span className="fs-6 text-body-secondary ms-1">
                                                        {selectedPlan.period}
                                                    </span>
                                                </span>
                                            </div>
                                            <p className="text-body-secondary small mb-0">
                                                {t.upgradePlaceholder}
                                            </p>
                                        </>
                                    ) : (
                                        <p className="text-body-secondary small mb-0">
                                            {t.selectPlanPlaceholder}
                                        </p>
                                    )}
                                </div>
                                <div className="d-flex gap-2 ms-auto flex-shrink-0">
                                    <button type="button" className="btn btn-outline-secondary" onClick={closeUpgradeModal}>
                                        {t.maybeLater}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={handleConfirmPlan}
                                        disabled={!selectedPlan || invoiceSubmitting}
                                    >
                                        {invoiceSubmitting
                                            ? t.creatingInvoice
                                            : selectedPlan
                                                ? t.continueWith(selectedPlan.name)
                                                : t.selectPlanPlaceholder}
                                    </button>
                                </div>
                            </div>
                        }
                        hideActions
                    >
                        <p className="text-body-secondary mb-4">{t.choosePlanSubtitle}</p>
                        {plansLoading && !pricingPlans.length && (
                            <div className="alert alert-info d-flex align-items-center gap-2 py-2 px-3 mb-4">
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                <span>{t.loadingPlans}</span>
                            </div>
                        )}
                        {plansError && (
                            <div className="alert alert-warning d-flex align-items-center gap-2 py-2 px-3 mb-4">
                                <i className="ti ti-alert-circle"></i>
                                <span>{plansError}</span>
                            </div>
                        )}
                        {invoiceError && (
                            <div className="alert alert-danger d-flex align-items-center gap-2 py-2 px-3 mb-4">
                                <i className="ti ti-alert-triangle"></i>
                                <span>{invoiceError}</span>
                            </div>
                        )}
                        <div className="mx-auto" style={{ maxWidth: "1200px" }}>
                            {plansLoading && !pricingPlans.length ? (
                                <div className="row g-4">
                                    {Array.from({ length: 3 }).map((_, idx) => (
                                        <div className="col-12 col-md-6 col-xl-4" key={`skeleton-${idx}`}>
                                            <div className="rounded-4 p-4 h-100 position-relative" style={{ background: "#f3f4f6", minHeight: 280 }}>
                                                <div className="placeholder-glow mb-3">
                                                    <span className="placeholder col-7"></span>
                                                </div>
                                                <div className="placeholder-glow mb-2">
                                                    <span className="placeholder col-5"></span>
                                                </div>
                                                <div className="placeholder-glow mb-4">
                                                    <span className="placeholder col-9"></span>
                                                    <span className="placeholder col-6"></span>
                                                </div>
                                                <div className="placeholder-wave">
                                                    <span className="placeholder col-12 mb-2"></span>
                                                    <span className="placeholder col-10 mb-2"></span>
                                                    <span className="placeholder col-8 mb-2"></span>
                                                </div>
                                                <div className="placeholder-glow mt-4">
                                                    <span className="placeholder col-6"></span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <>
                                    <div className="row g-4">
                                        {planOptions.map((plan) => (
                                            <PlanCard
                                                key={plan.id}
                                                plan={plan}
                                                selected={selectedPlan?.id === plan.id}
                                                onSelect={() => handlePlanSelect(plan.id)}
                                                selectLabel={t.btnSelectPlan}
                                                selectedLabel={t.btnSelected}
                                            />
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                </Dialog>
            )}

            {previewInvoice && (
                <InvoicePreviewDialog
                    invoice={previewInvoice}
                    t={t}
                    onClose={closePreview}
                    onPay={handlePayInvoice}
                    paying={payingInvoice}
                    payError={payError}
                />
            )}
        </>
    );
});

export default closeOnClickOutside(MenuUserProfile);
